import { useState, useCallback } from "react";
import type { ReactNode, PropsWithChildren } from "react";
import { Backdrop, Box, Grid } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { SearchCategories, SearchInput, SearchResults } from "components/Search/";
import CloseIcon from "@mui/icons-material/Close";

interface SearchInputsContainerProps extends PropsWithChildren {
  isOpen?: boolean;
  sx?: SxProps<Theme>;
}

function SearchInputsContainer({
  isOpen = false,
  sx,
  children,
}: Readonly<SearchInputsContainerProps>): ReactNode {
  return (
    <Grid
      container
      size={12}
      sx={{
        position: "absolute",
        top: 0,
        left: { xs: isOpen ? 0 : "60px", md: isOpen ? "60px" : "216px" },
        right: { xs: isOpen ? 0 : "60px", md: "60px" },
        height: isOpen ? "auto" : { sm: "64px", xs: "56px" },
        transition: "left .15s ease-out, right .15s ease-out",
        justifyContent: "center",
        ...sx,
      }}>
      <Grid
        container
        size={12}
        sx={{
          height: { sm: "64px", xs: "56px" },
          background: isOpen ? "white" : "transparent",
          transition: "background 0.1s ease-out",
          justifyContent: "center",
          alignItems: "center",
          maxWidth: "900px",
          position: "relative",
        }}>
        <Grid container size={{ xs: 11, md: 10 }} sx={{ justifyContent: "center" }} spacing={0}>
          {children}
        </Grid>
      </Grid>
    </Grid>
  );
}

interface CloseSearchProps {
  isOpen: boolean;
  handleClose: () => void;
}

function CloseSearch({ isOpen, handleClose }: Readonly<CloseSearchProps>): ReactNode {
  if (isOpen) {
    return (
      <>
        <Grid size="auto" sx={{ display: { md: "none" } }}>
          <Box width="30px" />
        </Grid>
        <Box
          sx={{
            position: "absolute",
            top: { xs: "8px", sm: "12px" },
            right: { xs: "8px", sm: "12px" },
            cursor: "pointer",
          }}
          onClick={handleClose}>
          <CloseIcon color="error" fontSize="large" />
        </Box>
      </>
    );
  } else {
    return null;
  }
}

function HeaderSearch(): ReactNode {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <>
      <SearchInputsContainer isOpen={isOpen} sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}>
        <SearchInput onFocus={handleOpen} size="grow" inputSize="small" />
        <CloseSearch isOpen={isOpen} handleClose={handleClose} />
      </SearchInputsContainer>
      <Backdrop
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isOpen}
        onClick={handleClose}>
        <Grid
          container
          size={12}
          sx={{
            position: "absolute",
            top: 0,
            left: { xs: isOpen ? 0 : "60px", md: isOpen ? "60px" : "216px" },
            right: { xs: isOpen ? 0 : "60px", md: "60px" },
            transition: "left .15s ease-out, right .15s ease-out",
            background: "none",
            overflowY: "visible",
            paddingTop: 0,
            justifyItems: "center",
            justifyContent: "center",
          }}>
          <Grid
            container
            size={12}
            sx={{
              height: "auto",
              maxHeight: isOpen ? "80vh" : 0,
              maxWidth: "900px",
              marginTop: { xs: "56px", sm: "64px" },
              paddingTop: isOpen ? 1 : 0,
              paddingBottom: isOpen ? 2 : 0,
              overflowY: "auto",
              background: "white",
              transition: "max-height 0.25s ease-out",
              justifyContent: "center",
              justifyItems: "center",
            }}>
            <Grid container size={{ xs: 11, md: 10 }} sx={{ justifyContent: "center" }} spacing={2}>
              <SearchCategories
                size={12}
                sx={{
                  position: "sticky",
                  zIndex: 2,
                  top: (theme) => theme.spacing(-2),
                  background: "white",
                  paddingY: 1,
                }}
              />
              <SearchResults size={12} />
            </Grid>
          </Grid>
        </Grid>
      </Backdrop>
    </>
  );
}

export default HeaderSearch;
